/**
 * Auth API — 세션 유지(refresh) / 로그아웃.
 *
 * BE: duckflex-peeka-be `AuthController` (`/api/v1/user/auth`)
 *   - POST /refresh
 *   - POST /logout         (refreshToken)
 *
 * 로그인·가입 자체는 소셜 전용이라 {@link ./social} 에 있다 (Google · Apple).
 * 이메일 로그인·비밀번호 재설정·카카오·네이버는 2026-08-04 에 제거됐다.
 */
import { apiPost } from "./client";
import type { AuthUser } from "../store/authStore";

// === BE TokenResponse (flat) ===
// duckflex-peeka-be AuthDto.TokenResponse 와 1:1 정합.
export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  accessExpiresIn: number;
  memberSerialNo: number;
  email: string;
  nickname: string | null;
}

export interface RefreshRequest {
  refreshToken: string;
}

export interface LogoutRequest {
  refreshToken: string;
}

// === Helpers ===

/** Flat TokenResponse → 클라이언트 AuthUser. provider 인자는 호출자가 명시. */
export function tokenResponseToAuthUser(
  res: TokenResponse,
  provider: AuthUser["provider"] = "google",
): AuthUser {
  return {
    memberSerialNo: res.memberSerialNo,
    email: res.email,
    nickname: res.nickname,
    provider,
  };
}

// === API ===

export const auth = {
  /**
   * access token 만료 시 새 토큰 발급.
   * 웹: 쿠키 자동전송 / 네이티브: SecureStorage refresh 를 X-Refresh-Token 헤더로 전송.
   *
   * ⚠️ 바디는 빈 객체 `{}` 를 보낸다(Content-Type: application/json 고정). undefined 로 보내면
   *    네이티브 CapacitorHttp 가 application/x-www-form-urlencoded 를 붙여 BE @RequestBody 가 415/500.
   */
  refresh: (refreshToken?: string) =>
    apiPost<TokenResponse>(
      "/auth/refresh",
      {},
      refreshToken ? { headers: { "X-Refresh-Token": refreshToken } } : undefined,
    ),

  /** server-side family 폐기. 웹=쿠키 / 네이티브=헤더. (바디 {} — 위 refresh 와 동일 이유) */
  logout: (refreshToken?: string) =>
    apiPost<void>(
      "/auth/logout",
      {},
      refreshToken ? { headers: { "X-Refresh-Token": refreshToken } } : undefined,
    ),
};

export type OAuthProvider = "google" | "apple";
